import { HTMLAttributes, SVGProps } from 'react';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';

import { Assets } from '@assets';

import { IconMap } from './iconMap';

export type IconVariant = 'feature' | 'primary' | 'secondary' | 'tertiary' | 'destructive';
export type IconSize = 'xs' | 'sm' | 'md' | 'lg';

export type IconName = keyof typeof IconMap | keyof typeof Assets;

export type IconProps = {
  name?: IconName;
  variant?: IconVariant;
  size?: IconSize;
} & HTMLAttributes<HTMLSpanElement>;

const iconVariant = tv({
  base: 'text-[1.5rem] [&>svg]:size-[1.5rem] fill-current ',
  variants: {
    variant: {
      feature: 'text-action',
      primary: 'text-action-dark-surface',
      secondary: 'text-action',
      tertiary: 'text-link-default',
      destructive: 'text-error',
    },
    size: {
      xs: 'text-[1rem] [&>svg]:size-[1rem]',
      sm: 'text-[1.25rem] [&>svg]:size-[1.25rem]',
      md: 'text-[1.5rem] [&>svg]:size-[1.5rem]',
      lg: 'text-[2rem] [&>svg]:size-[2rem]',
    },
  },
});

export const Icon = ({ name, size, variant, className, ...rest }: IconProps) => {
  if (!name) return null;
  if (name in Assets) {
    const IconComponent = Assets[name as keyof typeof Assets] as React.FC<SVGProps<SVGElement>>;
    return <IconComponent className={iconVariant({ size, variant, className })} />;
  }

  const IconComponent = IconMap[name as keyof typeof IconMap];

  return (
    <span
      {...rest}
      className={twMerge('material-symbols-outlined', iconVariant({ size, variant, className }))}>
      <IconComponent />
    </span>
  );
};
